// CaseWorkspace v1.0.6 - Search/filter functionality + Trust Referral Provider visibility fix
import { useState, useRef, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, FileText, MapPin, Clock, Users, Languages, Phone, Mail, Calendar, Building, Star, Target, Upload, Eye, Download, Trash2, Home, Briefcase, MessageSquare, UserCheck, Shield, X, Send, CheckCircle, XCircle, Loader2, Search, Filter, Heart } from "lucide-react";
import { CaseNotes } from "@/components/CaseNotes";
import { UnifiedCommunicationInbox } from "@/components/UnifiedCommunicationInbox";
import { ReferralAcceptance } from "@/components/ReferralAcceptance";
import { config } from "@/src/config";

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { authFetch } from '@/lib/authFetch';
const API_BASE_URL = config.api.baseUrl;

// Helper for authenticated fetch calls (includes X-CBO-ID for org-scoped data isolation)
const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token') || localStorage.getItem('member_token');
  const cboId = localStorage.getItem('active_cbo_id');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
    ...(cboId && { 'X-CBO-ID': cboId }),
  };
};

interface Referral {
  id: string | number;
  referralNumber?: string;
  // Member Information
  memberMedicaidId: string;
  memberName: string;
  dateOfBirth: string;
  zipCode: string;
  preferredLanguage: string;
  phoneNumber?: string;
  email?: string;
  streetAddress?: string;
  city?: string;
  state?: string;
  country?: string;

  // Health Plan Information
  healthPlan: string;
  planType: "Managed Care" | "Fee-for-Service";

  // Provider Information
  referringProvider: {
    name: string;
    npi: string;
    organization: string;
    phone: string;
  };

  // Clinical Context
  primaryDiagnosis: string;
  riskScore?: number;
  sdohNeeds: string[];
  housingSubcategories?: string[];
  transportationSubcategories?: string[];
  urgencyLevel: "Immediate" | "Within 24h" | "Within 1 Week" | "Routine";

  // Authorization & Compliance
  authorizationRequired: boolean;
  consentObtained: boolean;
  hipaaCompliant: boolean;

  // Legacy fields for backward compatibility
  timeline: string;
  status: "Pending" | "Matched" | "Auto-Matched" | "Accepted" | "Authorized" | "In Progress" | "On Hold" | "Completed";
  createdAt: string;
  timeAgo?: string;

  // Acceptance Workflow (PHI Integration)
  matchedProviderId?: string;
  acceptanceStatus?: 'pending' | 'accepted' | 'rejected' | 'expired';
  acceptedAt?: string;
  rejectedAt?: string;
  expiresAt?: string;
  phiEmailSent?: boolean;
  emailSentTo?: string;

  // CBO and Service Information
  cboName?: string;
  cbo_name?: string;  // API returns snake_case
  serviceType?: string;
}

interface Document {
  id: string;
  filename: string;
  file_type: string;
  file_size: number;
  uploaded_at: string;
  uploaded_by: number;
}

interface CaseWorkspaceProps {
  selectedReferralId: string | number | null;
  referrals: Referral[];
  onNavigateToDirectory: () => void;
  onBack?: () => void;
}

export function CaseWorkspace({ selectedReferralId, referrals, onNavigateToDirectory, onBack }: CaseWorkspaceProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  // Use string comparison to handle both number and string IDs
  const selectedReferral = referrals.find(r => String(r.id) === String(selectedReferralId));

  const [caseStatus, setCaseStatus] = useState(selectedReferral?.status || "Pending");
  const [nextAppointment, setNextAppointment] = useState("");
  const [documents, setDocuments] = useState<Document[]>([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<Document | null>(null);
  const [activeSection, setActiveSection] = useState<'timeline' | 'notes' | 'contacts' | 'documents' | 'communications' | 'acceptance'>('timeline');
  const [matchedProviders, setMatchedProviders] = useState<any[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Provider response workflow state
  const [sendingRequest, setSendingRequest] = useState<string | null>(null);
  const [providerResponseStatus, setProviderResponseStatus] = useState<Record<string, string>>({});
  const [showAcceptDialog, setShowAcceptDialog] = useState<string | null>(null);
  const [showRejectDialog, setShowRejectDialog] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  // Get CBO name from referral (handles both snake_case and camelCase)
  const getCboName = (referral: Referral): string => {
    return referral.cboName || referral.cbo_name || '';
  };

  // Filter referrals based on search and filters
  const filteredReferrals = useMemo(() => {
    return referrals.filter(referral => {
      // Search by member name
      const matchesSearch = searchQuery === '' ||
        referral.memberName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        referral.memberMedicaidId?.toLowerCase().includes(searchQuery.toLowerCase());

      // Filter by status
      const matchesStatus = statusFilter === 'all' || referral.status === statusFilter;

      // Filter by date
      let matchesDate = true;
      if (dateFilter !== 'all' && referral.createdAt) {
        const referralDate = new Date(referral.createdAt);
        const now = new Date();
        const daysDiff = Math.floor((now.getTime() - referralDate.getTime()) / (1000 * 60 * 60 * 24));

        switch (dateFilter) {
          case 'today':
            matchesDate = daysDiff === 0;
            break;
          case 'week':
            matchesDate = daysDiff <= 7;
            break;
          case 'month':
            matchesDate = daysDiff <= 30;
            break;
          case 'quarter':
            matchesDate = daysDiff <= 90;
            break;
        }
      }

      return matchesSearch && matchesStatus && matchesDate;
    });
  }, [referrals, searchQuery, statusFilter, dateFilter]);

  // Sync case status when referral changes
  useEffect(() => {
    if (selectedReferral?.status) {
      setCaseStatus(selectedReferral.status);
    }
  }, [selectedReferral?.status]);

  // Fetch documents when component loads
  const fetchDocuments = async () => {
    if (!selectedReferralId) {
      console.log('⚠️ fetchDocuments: No referral ID');
      return;
    }

    console.log('📥 Fetching documents for referral:', selectedReferralId);

    try {
      const response = await fetch(`${API_BASE_URL}/api/referrals/${selectedReferralId}/documents`, {
        headers: getAuthHeaders(),
      });
      console.log('📡 Fetch documents response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('📦 Documents data received:', data);
        console.log('📄 Number of documents:', data.documents?.length || 0);
        setDocuments(data.documents || []);
      } else {
        console.error('❌ Failed to fetch documents:', response.status);
      }
    } catch (error) {
      console.error('❌ Error fetching documents:', error);
    }
  };

  // Load documents on mount
  useEffect(() => {
    fetchDocuments();
  }, [selectedReferralId]);

  // Fetch matched providers
  const fetchMatchedProviders = async () => {
    if (!selectedReferralId) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/referrals/${selectedReferralId}/matches`, {
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('📋 Matched providers:', data);
        setMatchedProviders(data.matches || []);
      } else {
        console.error('❌ Failed to fetch matched providers:', response.status);
      }
    } catch (error) {
      console.error('❌ Error fetching matched providers:', error);
    }
  };

  // Load matched providers on mount
  useEffect(() => {
    fetchMatchedProviders();
  }, [selectedReferralId]);

  // Send referral acceptance request to provider
  const handleSendReferralRequest = async (providerId: string, providerName: string, providerEmail: string) => {
    setSendingRequest(providerId);
    try {
      // Generate token and send notification
      const response = await fetch(`${API_BASE_URL}/api/voice-workflows/referral-token/generate?referral_id=${selectedReferralId}&provider_id=${providerId}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const tokenData = await response.json();
        console.log('✅ Token generated:', tokenData);

        // Send email notification with acceptance links (use /api/referrals for care referrals)
        const emailResponse = await fetch(`${API_BASE_URL}/api/referrals/${selectedReferralId}/communicate`, {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            provider_id: providerId,
            channel: 'email',
            subject: `Referral Acceptance Request - ${selectedReferral?.memberName || 'Member'}`,
            content: `You have received a new referral from ${selectedReferral?.cboName || 'the organization'}.\n\nMember: ${selectedReferral?.memberName || 'N/A'}\nService Type: ${selectedReferral?.serviceType || 'Care Services'}\nLocation: ${selectedReferral?.zipCode || 'N/A'}\n\nClick below to respond:\n\n✅ Accept: ${tokenData.accept_url}\n❌ Decline: ${tokenData.reject_url}\n\nThis link expires in 72 hours.`,
            recipient: providerEmail,
          }),
        });

        if (emailResponse.ok) {
          setProviderResponseStatus(prev => ({ ...prev, [providerId]: 'notified' }));
          alert(`Referral request sent to ${providerName} at ${providerEmail}`);
        } else {
          throw new Error('Failed to send email');
        }
      } else {
        throw new Error('Failed to generate token');
      }
    } catch (error) {
      console.error('Error sending referral request:', error);
      alert('Failed to send referral request. Please try again.');
    } finally {
      setSendingRequest(null);
    }
  };

  // Accept referral on behalf of provider (demo mode)
  const handleDemoAccept = async (providerId: string, providerName: string) => {
    setSendingRequest(providerId);
    try {
      const response = await fetch(`${API_BASE_URL}/api/referrals/${selectedReferralId}/accept`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          provider_id: providerId,
          notes: `Accepted by ${providerName} (demo)`,
        }),
      });

      if (response.ok) {
        setProviderResponseStatus(prev => ({ ...prev, [providerId]: 'accepted' }));
        setShowAcceptDialog(null);
        alert(`Referral accepted by ${providerName}. PHI will be sent securely.`);
        window.location.reload();
      } else {
        throw new Error('Failed to accept referral');
      }
    } catch (error) {
      console.error('Error accepting referral:', error);
      alert('Failed to accept referral. Please try again.');
    } finally {
      setSendingRequest(null);
    }
  };

  // Reject referral on behalf of provider (demo mode)
  const handleDemoReject = async (providerId: string, providerName: string) => {
    if (!rejectReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    setSendingRequest(providerId);
    try {
      const response = await fetch(`${API_BASE_URL}/api/referrals/${selectedReferralId}/reject`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          provider_id: providerId,
          reason: rejectReason,
          notes: `Rejected by ${providerName} (demo)`,
        }),
      });

      if (response.ok) {
        setProviderResponseStatus(prev => ({ ...prev, [providerId]: 'rejected' }));
        setShowRejectDialog(null);
        setRejectReason('');
        alert(`Referral rejected by ${providerName}.`);
        window.location.reload();
      } else {
        throw new Error('Failed to reject referral');
      }
    } catch (error) {
      console.error('Error rejecting referral:', error);
      alert('Failed to reject referral. Please try again.');
    } finally {
      setSendingRequest(null);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    console.log('📁 File upload triggered, files:', files);

    if (!files || files.length === 0) {
      console.log('❌ No files selected');
      return;
    }

    if (!selectedReferralId) {
      console.log('❌ No referral selected');
      alert('No referral selected. Please select a referral first.');
      return;
    }

    const file = files[0];
    console.log('📄 File selected:', {
      name: file.name,
      type: file.type,
      size: file.size,
      referralId: selectedReferralId
    });

    const formData = new FormData();
    formData.append('file', file);
    formData.append('referral_id', String(selectedReferralId));
    formData.append('uploaded_by', '1'); // TODO: Get from auth context

    setUploadingFile(true);

    try {
      console.log('🚀 Uploading to:', `${API_BASE_URL}/api/referrals/${selectedReferralId}/documents`);
      const token = localStorage.getItem('auth_token') || localStorage.getItem('member_token');
      const cboId = localStorage.getItem('active_cbo_id');
      const response = await fetch(`${API_BASE_URL}/api/referrals/${selectedReferralId}/documents`, {
        method: 'POST',
        body: formData,
        headers: {
          // Note: Don't set Content-Type for FormData - browser sets it with boundary
          ...(token && { 'Authorization': `Bearer ${token}` }),
          ...(cboId && { 'X-CBO-ID': cboId }),
        },
      });

      console.log('📡 Upload response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ File uploaded successfully:', data);
        // Refresh documents list
        await fetchDocuments();
        console.log('🔄 Documents list refreshed');
        alert('Document uploaded successfully!');
      } else {
        const errorText = await response.text();
        console.error('❌ File upload failed:', errorText);
        alert(`Failed to upload file: ${errorText}`);
      }
    } catch (error) {
      console.error('❌ Error uploading file:', error);
      alert(`Error uploading file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setUploadingFile(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDownloadDocument = async (doc: Document) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/referrals/${selectedReferralId}/documents/${doc.id}`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Failed to download document.');
    }
  };

  const handleDeleteDocument = async (docId: string) => {
    if (!confirm('Are you sure you want to delete this document?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/referrals/${selectedReferralId}/documents/${docId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        await fetchDocuments();
      } else {
        alert('Failed to delete document.');
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Error deleting document.');
    }
  };

  const handlePreviewDocument = async (doc: Document) => {
    setPreviewDocument(doc);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getSDOHIcon = (need: string) => {
    if (need.toLowerCase().includes('housing')) return <Home className="w-4 h-4" />;
    if (need.toLowerCase().includes('food')) return <Heart className="w-4 h-4" />;
    if (need.toLowerCase().includes('transportation')) return <Building className="w-4 h-4" />;
    if (need.toLowerCase().includes('employment')) return <Briefcase className="w-4 h-4" />;
    return <Target className="w-4 h-4" />;
  };

  if (!selectedReferral) {
    return (
      <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        {/* Dynamic animated background */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-100/40 via-indigo-100/30 to-slate-100/40 animate-pulse"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/70 dark:via-gray-900/70 to-transparent"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-200/30 via-transparent to-transparent"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-indigo-200/20 via-transparent to-transparent"></div>

        {/* Content */}
        <div className="relative z-10 p-10 pt-4">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="w-full bg-gradient-to-r from-white/95 via-blue-50/90 to-indigo-50/95  pt-6 pb-8 px-8 border-b border-blue-200/50 shadow-xl rounded-2xl mb-8 relative">
              {/* Back Button */}
            {onBack && (
                <div className="absolute top-6 left-6">
              <Button
                variant="outline"
                onClick={onBack}
                    className="flex items-center space-x-2 bg-white dark:bg-gray-800 border-blue-300/50 hover:bg-blue-50/80 text-blue-800 shadow-sm hover:shadow-md transition-shadow rounded-xl"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>{t('workspace.back', 'Back')}</span>
              </Button>
                </div>
              )}

              <div className="max-w-6xl mx-auto">
                <div className="text-center">
                  <div className="inline-flex items-center space-x-3 mb-3">
                    <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-indigo-500 rounded-full"></div>
                    <h1 className="flex items-center justify-center space-x-3 text-4xl font-bold bg-gradient-to-r from-blue-800 to-indigo-800 bg-clip-text text-transparent">
                      <FileText className="w-8 h-8 text-blue-600" />
                <span>{t('workspace.case_workspace', 'Case Workspace')}</span>
              </h1>
                    <div className="w-1 h-8 bg-gradient-to-b from-indigo-500 to-blue-500 rounded-full"></div>
                  </div>
                  <p className="text-slate-600 text-lg font-medium mb-4">
                {t('workspace.manage_individual_case_details_and', 'Manage individual case details and progress')}
              </p>
            </div>
          </div>
            </div>

            {/* Search and Filter Bar */}
            <Card className="border border-blue-200/50 shadow-sm bg-white dark:bg-gray-800 rounded-xl overflow-hidden mb-6">
              <CardContent className="p-4">
                <div className="flex flex-col lg:flex-row gap-4">
                  {/* Search Input */}
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      placeholder={t('workspace.search_by_member_name_or_id', 'Search by member name or ID...')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 bg-gray-50 border-gray-200 focus:border-blue-300"
                    />
                  </div>

                  {/* Filter Toggle Button */}
                  <Button
                    variant="outline"
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center gap-2 ${showFilters ? 'bg-blue-50 border-blue-300' : ''}`}
                  >
                    <Filter className="w-4 h-4" />
                    {t('workspace.filters', 'Filters')}
                    {(statusFilter !== 'all' || dateFilter !== 'all') && (
                      <Badge className="bg-blue-600 text-white text-xs ml-1">
                        {[statusFilter !== 'all', dateFilter !== 'all'].filter(Boolean).length}
                      </Badge>
                    )}
                  </Button>
                </div>

                {/* Filter Options (Collapsible) */}
                {showFilters && (
                  <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Status Filter */}
                    <div>
                      <Label className="text-sm font-medium text-gray-700 mb-2 block">
                        {t('workspace.filter_by_status', 'Filter by Status')}
                      </Label>
                      <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-full bg-gray-50">
                          <SelectValue placeholder="All Statuses" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Statuses</SelectItem>
                          <SelectItem value="Pending">Pending</SelectItem>
                          <SelectItem value="Matched">Matched</SelectItem>
                          <SelectItem value="Auto-Matched">Auto-Matched</SelectItem>
                          <SelectItem value="Accepted">Accepted</SelectItem>
                          <SelectItem value="In Progress">In Progress</SelectItem>
                          <SelectItem value="On Hold">On Hold</SelectItem>
                          <SelectItem value="Completed">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Date Filter */}
                    <div>
                      <Label className="text-sm font-medium text-gray-700 mb-2 block">
                        {t('workspace.filter_by_date', 'Filter by Date')}
                      </Label>
                      <Select value={dateFilter} onValueChange={setDateFilter}>
                        <SelectTrigger className="w-full bg-gray-50">
                          <SelectValue placeholder="All Dates" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Dates</SelectItem>
                          <SelectItem value="today">Today</SelectItem>
                          <SelectItem value="week">Last 7 Days</SelectItem>
                          <SelectItem value="month">Last 30 Days</SelectItem>
                          <SelectItem value="quarter">Last 90 Days</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Clear Filters */}
                    {(statusFilter !== 'all' || dateFilter !== 'all' || searchQuery) && (
                      <div className="md:col-span-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSearchQuery('');
                            setStatusFilter('all');
                            setDateFilter('all');
                          }}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <X className="w-4 h-4 mr-1" />
                          {t('workspace.clear_all_filters', 'Clear all filters')}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Results Summary */}
                <div className="mt-3 text-sm text-gray-600">
                  {t('workspace.showing_results', 'Showing')} <span className="font-semibold text-blue-600">{filteredReferrals.length}</span> {t('workspace.of', 'of')} <span className="font-semibold">{referrals.length}</span> {t('workspace.referrals', 'referrals')}
                </div>
              </CardContent>
            </Card>

            {filteredReferrals.length === 0 ? (
              <Card className="border border-slate-200/50 shadow-sm bg-white dark:bg-gray-800 rounded-xl overflow-hidden">
                <CardContent className="p-12 text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-slate-500 to-slate-600 rounded-xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                    <Search className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">{t('workspace.no_matching_referrals', 'No matching referrals')}</h3>
                  <p className="text-slate-600 text-lg mb-4">
                    {t('workspace.try_adjusting_filters', 'Try adjusting your search or filter criteria.')}
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSearchQuery('');
                      setStatusFilter('all');
                      setDateFilter('all');
                    }}
                  >
                    <X className="w-4 h-4 mr-2" />
                    {t('workspace.clear_filters', 'Clear Filters')}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card className="border border-slate-200/50 shadow-sm bg-white dark:bg-gray-800 rounded-xl overflow-hidden">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4">{t('workspace.select_a_referral', 'Select a Referral')}</h3>
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {filteredReferrals.map((referral) => (
                      <div
                        key={String(referral.id)}
                        className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50/50 cursor-pointer transition-all"
                        onClick={() => {
                          // Navigate to this referral - this would need to be passed as a prop
                          window.location.hash = `#referral-${referral.id}`;
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-gray-800">{referral.memberName}</p>
                            <p className="text-sm text-gray-500">{referral.memberMedicaidId}</p>
                          </div>
                          <Badge
                            className={`${
                              referral.status === "Pending" ? "bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-200" :
                              referral.status === "Matched" ? "bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-200" :
                              referral.status === "Accepted" ? "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-200" :
                              referral.status === "In Progress" ? "bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-200" :
                              "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200"
                            }`}
                          >
                            {referral.status}
                          </Badge>
                        </div>
                        <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                          {referral.sdohNeeds && referral.sdohNeeds.length > 0 && (
                            <span className="flex items-center gap-1">
                              <Target className="w-3 h-3" />
                              {referral.sdohNeeds.slice(0, 2).join(', ')}
                              {referral.sdohNeeds.length > 2 && ` +${referral.sdohNeeds.length - 2}`}
                            </span>
                          )}
                          {referral.createdAt && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {fmt.short(referral.createdAt)}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    );
  }

  const getTimeAgo = (createdAt: string) => {
    const now = new Date();
    const created = new Date(createdAt);
    const diffInMinutes = Math.floor((now.getTime() - created.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes} min ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  // Handle case where referral is not found
  if (!selectedReferral) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">{t('workspace.referral_not_found', 'Referral Not Found')}</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">{t('workspace.the_selected_referral_could_not', 'The selected referral could not be found.')}</p>
            {onBack && (
              <Button onClick={onBack} variant="outline">
                {t('workspace.go_back', 'Go Back')}
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Static background - removed animations to prevent Teams/Zoom flickering */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-100/40 via-indigo-100/30 to-slate-100/40"></div>
      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/70 dark:via-gray-900/70 to-transparent"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-200/30 via-transparent to-transparent"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-indigo-200/20 via-transparent to-transparent"></div>

      {/* Content */}
      <div className="relative z-10 p-6">
        <div className="max-w-[1800px] mx-auto">
        {/* Compact Header */}
          <div className="w-full bg-gradient-to-r from-white/95 via-blue-50/90 to-indigo-50/95  py-4 px-6 border-b border-blue-200/50 shadow-xl rounded-2xl mb-6 relative">
            {/* Back Button */}
            {onBack && (
              <div className="absolute top-4 left-4 z-20">
              <Button
                variant="outline"
                size="sm"
                onClick={onBack}
                  className="flex items-center space-x-2 bg-white dark:bg-gray-800 border-blue-300/50 hover:bg-blue-50/80 text-blue-800 shadow-sm hover:shadow-md transition-shadow rounded-xl"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>{t('workspace.back_1', 'Back')}</span>
              </Button>
              </div>
            )}

            <div className="flex items-center justify-between" style={{ paddingLeft: onBack ? '120px' : '16px' }}>
              <div className="flex items-center space-x-4">
                <FileText className="w-6 h-6 text-blue-600" />
                <div>
                  <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-800 to-indigo-800 bg-clip-text text-transparent">
                    {t('workspace.case_workspace_1', 'Case Workspace')}
                  </h1>
                  <p className="text-sm text-slate-600 font-medium">
                    {selectedReferral.memberName} • {selectedReferral.memberMedicaidId}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
            {/* Show Find Providers button if status is Pending or On Hold */}
            {(selectedReferral.status === "Pending" || selectedReferral.status === "On Hold") && (
              <Button
                onClick={onNavigateToDirectory}
                      className="bg-white dark:bg-gray-800 border-blue-300/50 hover:bg-blue-50/80 text-blue-800 shadow-sm hover:shadow-md transition-shadow rounded-xl px-4 py-2 flex items-center space-x-2"
              >
                <Building className="w-4 h-4" />
                <span>{t('workspace.find_providers', 'Find Providers')}</span>
              </Button>
            )}
            <Badge
              variant={selectedReferral.status === "Pending" ? "secondary" : "default"}
                    className={`px-3 py-1.5 text-sm font-semibold rounded-xl ${
                      selectedReferral.status === "Pending"
                        ? "bg-gradient-to-r from-orange-100 to-orange-200 text-orange-800 border border-orange-300"
                        : selectedReferral.status === "Matched"
                        ? "bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800 border border-purple-300"
                        : selectedReferral.status === "Accepted"
                        ? "bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 border border-blue-300"
                        : selectedReferral.status === "In Progress"
                        ? "bg-gradient-to-r from-green-100 to-green-200 text-green-800 border border-green-300"
                        : selectedReferral.status === "On Hold"
                        ? "bg-gradient-to-r from-orange-100 to-orange-200 text-orange-800 border border-orange-300"
                        : "bg-gradient-to-r from-teal-100 to-teal-200 text-teal-800 border border-teal-300"
                    }`}
            >
              {selectedReferral.status}
            </Badge>
                </div>
              </div>
        </div>

        {/* Quick Actions - 4 columns side by side */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Call Member */}
          <Card className="bg-white dark:bg-gray-800 border border-blue-200/50 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex flex-col space-y-2">
                <div className="flex items-center space-x-2 text-blue-700 mb-1">
                  <Phone className="w-5 h-5" />
                  <span className="font-semibold">{t('workspace.call_member', 'Call Member')}</span>
                </div>
                {selectedReferral.phoneNumber ? (
                  <a
                    href={`tel:${selectedReferral.phoneNumber}`}
                    className="text-sm font-medium text-gray-800 dark:text-gray-100 hover:text-blue-600 transition-colors"
                  >
                    {selectedReferral.phoneNumber}
                  </a>
                ) : (
                  <span className="text-sm text-gray-500 italic">{t('workspace.no_information_available', 'No information available')}</span>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Send Email */}
          <Card className="bg-white dark:bg-gray-800 border border-purple-200/50 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex flex-col space-y-2">
                <div className="flex items-center space-x-2 text-purple-700 mb-1">
                  <Mail className="w-5 h-5" />
                  <span className="font-semibold">{t('workspace.send_email', 'Send Email')}</span>
                </div>
                {selectedReferral.email ? (
                  <a
                    href={`mailto:${selectedReferral.email}`}
                    className="text-sm font-medium text-gray-800 dark:text-gray-100 hover:text-purple-600 transition-colors truncate"
                    title={selectedReferral.email}
                  >
                    {selectedReferral.email}
                  </a>
                ) : (
                  <span className="text-sm text-gray-500 italic">{t('workspace.no_information_available_1', 'No information available')}</span>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Send SMS */}
          <Card className="bg-white dark:bg-gray-800 border border-green-200/50 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex flex-col space-y-2">
                <div className="flex items-center space-x-2 text-green-700 mb-1">
                  <MessageSquare className="w-5 h-5" />
                  <span className="font-semibold">{t('workspace.send_sms', 'Send SMS')}</span>
                </div>
                {selectedReferral.phoneNumber ? (
                  <a
                    href={`sms:${selectedReferral.phoneNumber}`}
                    className="text-sm font-medium text-gray-800 dark:text-gray-100 hover:text-green-600 transition-colors"
                  >
                    {selectedReferral.phoneNumber}
                  </a>
                ) : (
                  <span className="text-sm text-gray-500 italic">{t('workspace.no_information_available_2', 'No information available')}</span>
                )}
              </div>
            </CardContent>
          </Card>

          {/* View Address */}
          <Card className="bg-white dark:bg-gray-800 border border-orange-200/50 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex flex-col space-y-2">
                <div className="flex items-center space-x-2 text-orange-700 mb-1">
                  <MapPin className="w-5 h-5" />
                  <span className="font-semibold">{t('workspace.address', 'Address')}</span>
                </div>
                {selectedReferral.streetAddress ? (
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${selectedReferral.streetAddress}, ${selectedReferral.city}, ${selectedReferral.state} ${selectedReferral.zipCode}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-gray-800 dark:text-gray-100 hover:text-orange-600 transition-colors line-clamp-2"
                    title={`${selectedReferral.streetAddress}, ${selectedReferral.city}, ${selectedReferral.state}`}
                  >
                    {selectedReferral.streetAddress}, {selectedReferral.city}
                  </a>
                ) : (
                  <span className="text-sm text-gray-500 italic">{t('workspace.no_information_available_3', 'No information available')}</span>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
          {/* Main Content - Takes 3 columns */}
          <div className="xl:col-span-3 space-y-6">
            {/* Enhanced Case Overview - More compact and informative */}
              <Card className="border border-blue-200/50 dark:border-gray-700 shadow-sm bg-white dark:bg-gray-800 rounded-2xl overflow-hidden">
                <CardHeader className="pb-4 bg-gradient-to-r from-blue-50/90 to-indigo-50/90 border-b border-blue-200/40">
                  <CardTitle className="flex items-center space-x-2 text-lg font-semibold text-gray-800 dark:text-gray-100">
                    <div className="p-2 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl shadow-sm">
                      <Users className="w-5 h-5 text-white" />
                    </div>
                  <span>{t('workspace.case_overview', 'Case Overview')}</span>
                </CardTitle>
              </CardHeader>
                <CardContent className="p-6">
                  {/* Comprehensive Grid Layout */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Contact Information */}
                    <div className="col-span-2 lg:col-span-4 bg-gradient-to-r from-blue-50/50 to-indigo-50/50 rounded-xl p-4 border border-blue-200/30">
                      <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center">
                        <Phone className="w-4 h-4 mr-2 text-blue-600" />
                        {t('workspace.contact_information', 'Contact Information')}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                        {selectedReferral.phoneNumber && (
                          <div className="flex items-center space-x-2">
                            <Phone className="w-3.5 h-3.5 text-green-600" />
                            <span className="font-medium text-gray-800 dark:text-gray-100">{selectedReferral.phoneNumber}</span>
                          </div>
                        )}
                        {selectedReferral.email && (
                          <div className="flex items-center space-x-2">
                            <Mail className="w-3.5 h-3.5 text-purple-600" />
                            <span className="font-medium text-gray-800 dark:text-gray-100 truncate">{selectedReferral.email}</span>
                          </div>
                        )}
                        {selectedReferral.streetAddress && (
                          <div className="flex items-center space-x-2">
                            <MapPin className="w-3.5 h-3.5 text-red-600" />
                            <span className="font-medium text-gray-800 dark:text-gray-100 truncate">
                              {selectedReferral.streetAddress}, {selectedReferral.city}, {selectedReferral.state}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* SDOH Needs - Prominent display */}
                    <div className="col-span-2 lg:col-span-4 bg-gradient-to-r from-green-50/50 to-emerald-50/50 rounded-xl p-4 border border-green-200/30">
                      <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center">
                        <Target className="w-4 h-4 mr-2 text-green-600" />
                        {t('workspace.social_determinants_of_health_needs', 'Social Determinants of Health Needs')}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedReferral.sdohNeeds.map((need, idx) => (
                          <Badge key={idx} className="bg-green-100 text-green-800 border-green-300 dark:bg-green-900/50 dark:text-green-200 dark:border-green-700 px-3 py-1 flex items-center space-x-1.5">
                            {getSDOHIcon(need)}
                            <span>{need}</span>
                          </Badge>
                        ))}
                        {selectedReferral.housingSubcategories && selectedReferral.housingSubcategories.length > 0 && (
                          <div className="w-full mt-2 text-xs text-gray-600 dark:text-gray-300">
                            <span className="font-semibold">{t('workspace.housing', 'Housing:')}</span> {selectedReferral.housingSubcategories.join(', ')}
                          </div>
                        )}
                        {selectedReferral.transportationSubcategories && selectedReferral.transportationSubcategories.length > 0 && (
                          <div className="w-full mt-1 text-xs text-gray-600 dark:text-gray-300">
                            <span className="font-semibold">{t('workspace.transportation', 'Transportation:')}</span> {selectedReferral.transportationSubcategories.join(', ')}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Member Details - Compact grid */}
                    <div className="bg-blue-50/50 rounded-lg p-3 border border-blue-200/30">
                      <Label className="text-xs font-semibold text-gray-600 mb-1 block">{t('workspace.date_of_birth', 'Date of Birth')}</Label>
                      <div className="text-sm font-bold text-gray-800 dark:text-gray-100">{selectedReferral.dateOfBirth}</div>
                    </div>

                    <div className="bg-purple-50/50 rounded-lg p-3 border border-purple-200/30">
                      <Label className="text-xs font-semibold text-gray-600 mb-1 block flex items-center">
                        <Languages className="w-3.5 h-3.5 mr-1" />
                        {t('workspace.language', 'Language')}
                      </Label>
                      <div className="text-sm font-bold text-gray-800 dark:text-gray-100">{selectedReferral.preferredLanguage}</div>
                    </div>

                    <div className="bg-orange-50/50 rounded-lg p-3 border border-orange-200/30">
                      <Label className="text-xs font-semibold text-gray-600 mb-1 block flex items-center">
                        <Clock className="w-3.5 h-3.5 mr-1" />
                        {t('workspace.urgency', 'Urgency')}
                      </Label>
                      <div className="text-sm font-bold text-gray-800 dark:text-gray-100">{selectedReferral.urgencyLevel}</div>
                    </div>

                    <div className="bg-slate-50/50 rounded-lg p-3 border border-slate-200/30">
                      <Label className="text-xs font-semibold text-gray-600 mb-1 block flex items-center">
                        <Calendar className="w-3.5 h-3.5 mr-1" />
                        {t('workspace.created', 'Created')}
                      </Label>
                      <div className="text-sm font-bold text-gray-800 dark:text-gray-100">{getTimeAgo(selectedReferral.createdAt)}</div>
                    </div>

                    {/* Provider Information */}
                    <div className="col-span-2 bg-indigo-50/50 rounded-lg p-3 border border-indigo-200/30">
                      <Label className="text-xs font-semibold text-gray-600 mb-2 block">{t('workspace.referring_provider', 'Referring Provider')}</Label>
                      <div className="space-y-1 text-sm">
                        <div className="font-bold text-gray-800 dark:text-gray-100">{selectedReferral.referringProvider.name}</div>
                        <div className="text-gray-600 dark:text-gray-400">{selectedReferral.referringProvider.organization}</div>
                        <div className="text-gray-600 dark:text-gray-400">NPI: {selectedReferral.referringProvider.npi}</div>
                      </div>
                    </div>

                    {/* Health Plan */}
                    <div className="col-span-2 bg-teal-50/50 rounded-lg p-3 border border-teal-200/30">
                      <Label className="text-xs font-semibold text-gray-600 mb-2 block">{t('workspace.health_plan', 'Health Plan')}</Label>
                      <div className="space-y-1 text-sm">
                        <div className="font-bold text-gray-800 dark:text-gray-100">{selectedReferral.healthPlan}</div>
                        <div className="text-gray-600 dark:text-gray-400">{selectedReferral.planType}</div>
                      </div>
                    </div>
                  </div>
              </CardContent>
            </Card>

            {/* Matched Providers Section - Show if referral has matched providers */}
            {matchedProviders && matchedProviders.length > 0 && (
              <Card className="border border-teal-200/50 dark:border-gray-700 shadow-sm bg-white dark:bg-gray-800 rounded-2xl overflow-hidden">
                <CardHeader className="pb-4 bg-gradient-to-r from-teal-50/90 to-green-50/90 border-b border-teal-200/40">
                  <CardTitle className="flex items-center space-x-2 text-lg font-semibold text-gray-800 dark:text-gray-100">
                    <div className="p-2 bg-gradient-to-br from-teal-600 to-green-600 rounded-xl shadow-sm">
                      <Users className="w-5 h-5 text-white" />
                    </div>
                    <span>Matched Providers ({matchedProviders.length})</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {matchedProviders.map((provider: any, idx: number) => (
                      <div
                        key={provider.match_id}
                        className={`rounded-lg border p-4 ${
                          provider.acceptance_status === 'accepted'
                            ? 'border-green-300 bg-green-50/50'
                            : provider.acceptance_status === 'rejected'
                            ? 'border-red-300 bg-red-50/50'
                            : provider.acceptance_status === 'expired'
                            ? 'border-gray-300 bg-gray-50/50'
                            : 'border-blue-300 bg-blue-50/50'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h5 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">
                              {provider.provider_name}
                            </h5>
                            <div className="flex items-center space-x-2 mt-1 text-xs text-gray-600 dark:text-gray-400">
                              <Star className="w-3 h-3" />
                              <span>Match Score: {provider.match_score.toFixed(1)}%</span>
                            </div>
                          </div>
                          <span className={`px-2 py-1 rounded-md text-xs font-semibold ${
                            provider.acceptance_status === 'accepted'
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-200'
                              : provider.acceptance_status === 'rejected'
                              ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-200'
                              : provider.acceptance_status === 'expired'
                              ? 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
                              : 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-200'
                          }`}>
                            {provider.acceptance_status}
                          </span>
                        </div>

                        {/* Provider Contact Info */}
                        <div className="space-y-1 text-xs text-gray-600 dark:text-gray-400">
                          {provider.provider_email && (
                            <div className="flex items-center space-x-2">
                              <Mail className="w-3 h-3" />
                              <span>{provider.provider_email}</span>
                            </div>
                          )}
                          {provider.provider_phone && (
                            <div className="flex items-center space-x-2">
                              <Phone className="w-3 h-3" />
                              <span>{provider.provider_phone}</span>
                            </div>
                          )}
                          {provider.provider_address && (
                            <div className="flex items-center space-x-2">
                              <MapPin className="w-3 h-3" />
                              <span>{provider.provider_address}</span>
                            </div>
                          )}
                        </div>

                        {/* Matched timestamp */}
                        {provider.matched_at && (
                          <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                            <Clock className="w-3 h-3 inline mr-1" />
                            Matched: {fmt.short(provider.matched_at)}
                          </div>
                        )}

                        {/* Response timestamp */}
                        {provider.accepted_at && (
                          <div className="mt-1 text-xs text-green-600">
                            <CheckCircle className="w-3 h-3 inline mr-1" />
                            Accepted: {fmt.short(provider.accepted_at)}
                          </div>
                        )}
                        {provider.rejected_at && (
                          <div className="mt-1 text-xs text-red-600">
                            <Clock className="w-3 h-3 inline mr-1" />
                            Rejected: {fmt.short(provider.rejected_at)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {(selectedReferral as any).accepted_provider_id && (
                    <div className="mt-4 p-3 bg-green-100 border border-green-300 rounded-lg">
                      <div className="flex items-center space-x-2 text-green-800">
                        <Star className="w-4 h-4" />
                        <span className="font-semibold text-sm">
                          Provider Accepted: {
                            matchedProviders.find(
                              (p: any) => p.acceptance_status === 'accepted'
                            )?.provider_name || 'Unknown Provider'
                          }
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Case Management Section - Button Navigation */}
            <div className="w-full">
              {/* Horizontal Menu Buttons - scrollable on mobile */}
              <div className="flex gap-2 bg-white dark:bg-gray-800 border border-blue-200/50 dark:border-gray-700 rounded-xl p-2 shadow-sm mb-4 overflow-x-auto scrollbar-hide">
                <Button
                  onClick={() => setActiveSection('timeline')}
                  className={`flex-shrink-0 rounded-lg transition-all duration-300 ${
                    activeSection === 'timeline'
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-sm'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-400 hover:bg-blue-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <Clock className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span className="whitespace-nowrap">{t('workspace.timeline', 'Timeline')}</span>
                </Button>
                <Button
                  onClick={() => setActiveSection('notes')}
                  className={`flex-shrink-0 rounded-lg transition-all duration-300 ${
                    activeSection === 'notes'
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-sm'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-400 hover:bg-blue-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <FileText className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span className="whitespace-nowrap">{t('workspace.notes', 'Notes')}</span>
                </Button>
                <Button
                  onClick={() => setActiveSection('communications')}
                  className={`flex-shrink-0 rounded-lg transition-all duration-300 ${
                    activeSection === 'communications'
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-sm'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-400 hover:bg-blue-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <MessageSquare className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span className="whitespace-nowrap">{t('workspace.communications', 'Communications')}</span>
                </Button>
                <Button
                  onClick={() => setActiveSection('contacts')}
                  className={`flex-shrink-0 rounded-lg transition-all duration-300 ${
                    activeSection === 'contacts'
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-sm'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-400 hover:bg-blue-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <Phone className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span className="whitespace-nowrap">{t('workspace.contacts', 'Contacts')}</span>
                </Button>
                <Button
                  onClick={() => setActiveSection('documents')}
                  className={`flex-shrink-0 rounded-lg transition-all duration-300 ${
                    activeSection === 'documents'
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-sm'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-400 hover:bg-blue-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <Upload className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span className="whitespace-nowrap">{t('workspace.documents', 'Documents')}</span>
                </Button>
                <Button
                  onClick={() => setActiveSection('acceptance')}
                  className={`flex-shrink-0 rounded-lg transition-all duration-300 ${
                    activeSection === 'acceptance'
                      ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-sm'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-400 hover:bg-purple-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <UserCheck className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span className="whitespace-nowrap">{t('workspace.acceptance', 'Provider Response')}</span>
                </Button>
              </div>

              {/* Timeline Section */}
              {activeSection === 'timeline' && (
                  <Card className="border border-blue-200/50 dark:border-gray-700 shadow-xl bg-white dark:bg-gray-800 rounded-2xl overflow-hidden min-h-[600px]">
                    <CardHeader className="pb-4 bg-gradient-to-r from-blue-50/90 to-indigo-50/90 border-b border-blue-200/40">
                      <CardTitle className="flex items-center space-x-2 text-lg font-semibold text-gray-800 dark:text-gray-100">
                        <div className="p-2 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl shadow-sm">
                          <Clock className="w-5 h-5 text-white" />
                        </div>
                        <span>{t('workspace.case_timeline', 'Case Timeline')}</span>
                      </CardTitle>
                  </CardHeader>
                    <CardContent className="p-6">
                      <div className="space-y-4">
                        {/* Case Created */}
                        <div className="flex items-start space-x-3">
                          <div className="w-3 h-3 bg-gradient-to-r from-green-500 to-green-600 rounded-full mt-2 shadow-sm flex-shrink-0"></div>
                          <div className="flex-1 p-4 bg-gradient-to-r from-green-50/80 to-emerald-50/80 border border-green-200/50 rounded-xl">
                            <div className="font-semibold text-base text-gray-800 dark:text-gray-100 mb-1">{t('workspace.referral_created', 'Referral Created')}</div>
                            <div className="text-sm text-slate-600 font-medium mb-2">{getTimeAgo(selectedReferral.createdAt)}</div>
                            <p className="text-sm text-slate-700">
                              Referral received for {selectedReferral.memberName}
                            </p>
                            <p className="text-xs text-slate-600 mt-1">
                              {t('workspace.status', 'Status:')} <span className="font-semibold">{t('workspace.pending', 'Pending')}</span>
                            </p>
                          </div>
                        </div>

                        {/* Show Matched status if applicable */}
                        {(selectedReferral.status === "Matched" || selectedReferral.status === "Auto-Matched" || selectedReferral.status === "Accepted" ||
                          selectedReferral.status === "In Progress" || selectedReferral.status === "On Hold" ||
                          selectedReferral.status === "Completed") && (
                          <div className="flex items-start space-x-3">
                            <div className={`w-3 h-3 ${selectedReferral.status === "Auto-Matched" ? "bg-gradient-to-r from-teal-500 to-cyan-600" : "bg-gradient-to-r from-purple-500 to-purple-600"} rounded-full mt-2 shadow-sm flex-shrink-0`}></div>
                            <div className={`flex-1 p-4 ${selectedReferral.status === "Auto-Matched" ? "bg-gradient-to-r from-teal-50/80 to-cyan-50/80 border border-teal-200/50" : "bg-gradient-to-r from-purple-50/80 to-violet-50/80 border border-purple-200/50"} rounded-xl`}>
                              <div className="font-semibold text-base text-gray-800 dark:text-gray-100 mb-1">
                                {matchedProviders.length > 0 ? (
                                  <>
                                    Matched to {matchedProviders.length} Provider{matchedProviders.length > 1 ? 's' : ''}
                                    <span className="text-sm font-normal text-slate-600 ml-2">
                                      ({matchedProviders.length === 1 ? 'manual match' : 'auto-matched'})
                                    </span>
                                  </>
                                ) : (
                                  'Provider Matched'
                                )}
                              </div>
                              <div className="text-sm text-slate-600 font-medium mb-2">{t('workspace.recently', 'Recently')}</div>
                              {matchedProviders.length > 0 ? (
                                <>
                                  <p className="text-sm text-slate-700 mb-2">
                                    {t('workspace.referral_successfully_matched_to', 'Referral successfully matched to:')}
                                  </p>
                                  <ul className="text-xs text-slate-700 space-y-1 mb-2">
                                    {matchedProviders.slice(0, 4).map((provider: any, idx: number) => (
                                      <li key={idx} className="flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 bg-purple-400 rounded-full"></span>
                                        <span className="font-medium">{provider.provider_name}</span>
                                        <span className="text-slate-500">({provider.match_score.toFixed(1)}% match)</span>
                                      </li>
                                    ))}
                                  </ul>
                                </>
                              ) : (
                                <p className="text-sm text-slate-700">
                                  {t('workspace.referral_successfully_matched_to_a', 'Referral successfully matched to a provider')}
                                </p>
                              )}
                              <p className="text-xs text-slate-600 mt-1">
                                {t('workspace.status_1', 'Status:')} <span className={`font-semibold ${selectedReferral.status === "Auto-Matched" ? "text-teal-700" : "text-purple-700"}`}>{selectedReferral.status}</span>
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Provider Response Required - Acceptance Workflow (PHI Integration) */}
                        {selectedReferral.status === "Matched" && selectedReferral.matchedProviderId && selectedReferral.acceptanceStatus === 'pending' && (
                          <div className="flex items-start space-x-3">
                            <div className="w-3 h-3 bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-full mt-2 shadow-sm flex-shrink-0 animate-pulse"></div>
                            <div className="flex-1 p-4 bg-gradient-to-r from-indigo-50/80 to-purple-50/80 border-2 border-indigo-300/50 rounded-xl">
                              <div className="font-semibold text-base text-gray-800 mb-3">🛡️ Provider Response Required</div>
                              <ReferralAcceptance
                                referralId={String(selectedReferral.id)}
                                providerId={selectedReferral.matchedProviderId}
                                onAccept={() => {
                                  console.log('Referral accepted from timeline');
                                  window.location.reload();
                                }}
                                onReject={() => {
                                  console.log('Referral rejected from timeline');
                                  window.location.reload();
                                }}
                              />
                            </div>
                          </div>
                        )}

                        {/* Acceptance Status Timeline Events */}
                        {selectedReferral.acceptanceStatus === 'accepted' && selectedReferral.matchedProviderId && (
                          <div className="flex items-start space-x-3">
                            <div className="w-3 h-3 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full mt-2 shadow-sm flex-shrink-0"></div>
                            <div className="flex-1 p-4 bg-gradient-to-r from-emerald-50/80 to-green-50/80 border border-emerald-200/50 rounded-xl">
                              <div className="font-semibold text-base text-gray-800 dark:text-gray-100 mb-1">✓ Provider Accepted Referral</div>
                              <div className="text-sm text-slate-600 font-medium mb-2">
                                {selectedReferral.acceptedAt && fmt.dateTime(selectedReferral.acceptedAt)}
                              </div>
                              {/* Full ReferralAcceptance component for PHI disclosure display */}
                              <ReferralAcceptance
                                referralId={String(selectedReferral.id)}
                                providerId={selectedReferral.matchedProviderId}
                                onAccept={() => {}}
                                onReject={() => {}}
                              />
                            </div>
                          </div>
                        )}

                        {selectedReferral.acceptanceStatus === 'rejected' && (
                          <div className="flex items-start space-x-3">
                            <div className="w-3 h-3 bg-gradient-to-r from-red-500 to-red-600 rounded-full mt-2 shadow-sm flex-shrink-0"></div>
                            <div className="flex-1 p-4 bg-gradient-to-r from-red-50/80 to-rose-50/80 border border-red-200/50 rounded-xl">
                              <div className="font-semibold text-base text-gray-800 dark:text-gray-100 mb-1">✗ Provider Rejected Referral</div>
                              <div className="text-sm text-slate-600 font-medium mb-2">
                                {selectedReferral.rejectedAt && fmt.dateTime(selectedReferral.rejectedAt)}
                              </div>
                              <p className="text-sm text-slate-700">
                                {t('workspace.provider_declined_the_referral_will', 'Provider declined the referral - will need to re-match')}
                              </p>
                            </div>
                          </div>
                        )}

                        {selectedReferral.acceptanceStatus === 'expired' && (
                          <div className="flex items-start space-x-3">
                            <div className="w-3 h-3 bg-gradient-to-r from-gray-500 to-gray-600 rounded-full mt-2 shadow-sm flex-shrink-0"></div>
                            <div className="flex-1 p-4 bg-gradient-to-r from-gray-50/80 to-slate-50/80 border border-gray-200/50 rounded-xl">
                              <div className="font-semibold text-base text-gray-800 dark:text-gray-100 mb-1">⏰ Referral Expired</div>
                              <div className="text-sm text-slate-600 font-medium mb-2">
                                {selectedReferral.expiresAt && fmt.dateTime(selectedReferral.expiresAt)}
                              </div>
                              <p className="text-sm text-slate-700">
                                {t('workspace.provider_did_not_respond_within', 'Provider did not respond within 7 days - will need to re-match')}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Show Accepted status if applicable */}
                        {(selectedReferral.status === "Accepted" || selectedReferral.status === "In Progress" ||
                          selectedReferral.status === "On Hold" || selectedReferral.status === "Completed") && (
                          <div className="flex items-start space-x-3">
                            <div className="w-3 h-3 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full mt-2 shadow-sm flex-shrink-0"></div>
                            <div className="flex-1 p-4 bg-gradient-to-r from-blue-50/80 to-indigo-50/80 border border-blue-200/50 rounded-xl">
                              <div className="font-semibold text-base text-gray-800 dark:text-gray-100 mb-1">{t('workspace.referral_accepted_by_provider', 'Referral Accepted by Provider')}</div>
                              <div className="text-sm text-slate-600 font-medium mb-2">{t('workspace.recently_1', 'Recently')}</div>
                              <p className="text-sm text-slate-700">
                                {t('workspace.provider_has_accepted_the_referral_1', 'Provider has accepted the referral and will begin services')}
                              </p>
                              <p className="text-xs text-slate-600 mt-1">
                                {t('workspace.status_2', 'Status:')} <span className="font-semibold text-blue-700">{t('workspace.accepted', 'Accepted')}</span>
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Show In Progress status if applicable */}
                        {(selectedReferral.status === "In Progress" || selectedReferral.status === "On Hold" ||
                          selectedReferral.status === "Completed") && (
                          <div className="flex items-start space-x-3">
                            <div className="w-3 h-3 bg-gradient-to-r from-green-500 to-green-600 rounded-full mt-2 shadow-sm flex-shrink-0"></div>
                            <div className="flex-1 p-4 bg-gradient-to-r from-green-50/80 to-emerald-50/80 border border-green-200/50 rounded-xl">
                              <div className="font-semibold text-base text-gray-800 dark:text-gray-100 mb-1">{t('workspace.services_in_progress', 'Services In Progress')}</div>
                              <div className="text-sm text-slate-600 font-medium mb-2">{t('workspace.recently_2', 'Recently')}</div>
                              <p className="text-sm text-slate-700">
                                {t('workspace.member_is_currently_receiving_services', 'Member is currently receiving services from provider')}
                              </p>
                              <p className="text-xs text-slate-600 mt-1">
                                {t('workspace.status_3', 'Status:')} <span className="font-semibold text-green-700">{t('workspace.in_progress', 'In Progress')}</span>
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Show On Hold status if applicable */}
                        {selectedReferral.status === "On Hold" && (
                          <div className="flex items-start space-x-3">
                            <div className="w-3 h-3 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full mt-2 shadow-sm flex-shrink-0"></div>
                            <div className="flex-1 p-4 bg-gradient-to-r from-orange-50/80 to-amber-50/80 border border-orange-200/50 rounded-xl">
                              <div className="font-semibold text-base text-gray-800 dark:text-gray-100 mb-1">{t('workspace.case_on_hold', 'Case On Hold')}</div>
                              <div className="text-sm text-slate-600 font-medium mb-2">{t('workspace.recently_3', 'Recently')}</div>
                              <p className="text-sm text-slate-700">
                                {t('workspace.case_has_been_temporarily_put', 'Case has been temporarily put on hold')}
                              </p>
                              <p className="text-xs text-slate-600 mt-1">
                                {t('workspace.status_4', 'Status:')} <span className="font-semibold text-orange-700">{t('workspace.on_hold', 'On Hold')}</span>
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Show Completed status if applicable */}
                        {selectedReferral.status === "Completed" && (
                          <div className="flex items-start space-x-3">
                            <div className="w-3 h-3 bg-gradient-to-r from-teal-500 to-teal-600 rounded-full mt-2 shadow-sm flex-shrink-0"></div>
                            <div className="flex-1 p-4 bg-gradient-to-r from-teal-50/80 to-cyan-50/80 border border-teal-200/50 rounded-xl">
                              <div className="font-semibold text-base text-gray-800 dark:text-gray-100 mb-1">{t('workspace.case_completed', 'Case Completed')}</div>
                              <div className="text-sm text-slate-600 font-medium mb-2">{t('workspace.recently_4', 'Recently')}</div>
                              <p className="text-sm text-slate-700">
                                {t('workspace.services_completed_successfully', 'Services completed successfully')}
                              </p>
                              <p className="text-xs text-slate-600 mt-1">
                                {t('workspace.status_5', 'Status:')} <span className="font-semibold text-teal-700">{t('workspace.completed', 'Completed')}</span>
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                </Card>
              )}

              {/* Notes Section */}
              {activeSection === 'notes' && (
                <CaseNotes
                  referralId={String(selectedReferral.id)}
                  userId={1}
                />
              )}

              {/* Communications Section */}
              {activeSection === 'communications' && (
                <Card className="border border-blue-200/50 dark:border-gray-700 shadow-xl bg-white dark:bg-gray-800 rounded-2xl overflow-hidden h-[700px] flex flex-col">
                  <CardHeader className="pb-4 bg-gradient-to-r from-blue-50/90 to-indigo-50/90 border-b border-blue-200/40">
                    <CardTitle className="flex items-center space-x-2 text-lg font-semibold text-gray-800 dark:text-gray-100">
                      <div className="p-2 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl shadow-sm">
                        <MessageSquare className="w-5 h-5 text-white" />
                      </div>
                      <span>{t('workspace.communications_1', 'Communications')}</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex-1 overflow-hidden p-0">
                    <UnifiedCommunicationInbox
                      referralId={selectedReferral.id}
                    />
                  </CardContent>
                </Card>
              )}

              {/* Contacts Section */}
              {activeSection === 'contacts' && (
                  <Card className="border border-blue-200/50 dark:border-gray-700 shadow-xl bg-white dark:bg-gray-800 rounded-2xl overflow-hidden">
                    <CardHeader className="pb-4 bg-gradient-to-r from-blue-50/90 to-indigo-50/90 border-b border-blue-200/40">
                      <CardTitle className="flex items-center space-x-2 text-lg font-semibold text-gray-800 dark:text-gray-100">
                        <div className="p-2 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl shadow-sm">
                          <Phone className="w-5 h-5 text-white" />
                        </div>
                        <span>{t('workspace.contact_information_1', 'Contact Information')}</span>
                      </CardTitle>
                  </CardHeader>
                    <CardContent className="p-6 space-y-4">
                      {/* Show existing contact info from referral */}
                      <div className="bg-blue-50/50 rounded-xl p-4 border border-blue-200/30 mb-4">
                        <h3 className="text-sm font-bold text-gray-700 mb-3">{t('workspace.member_contact_details', 'Member Contact Details')}</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          {selectedReferral.phoneNumber && (
                            <div>
                              <Label className="text-xs text-gray-600 dark:text-gray-300">{t('workspace.phone_number', 'Phone Number')}</Label>
                              <div className="font-bold text-gray-800 dark:text-gray-100 mt-1">{selectedReferral.phoneNumber}</div>
                            </div>
                          )}
                          {selectedReferral.email && (
                            <div>
                              <Label className="text-xs text-gray-600 dark:text-gray-300">{t('workspace.email_address', 'Email Address')}</Label>
                              <div className="font-bold text-gray-800 dark:text-gray-100 mt-1">{selectedReferral.email}</div>
                            </div>
                          )}
                          {selectedReferral.streetAddress && (
                            <div className="col-span-2">
                              <Label className="text-xs text-gray-600 dark:text-gray-300">{t('workspace.address_1', 'Address')}</Label>
                              <div className="font-bold text-gray-800 dark:text-gray-100 mt-1">
                                {selectedReferral.streetAddress}<br />
                                {selectedReferral.city}, {selectedReferral.state} {selectedReferral.zipCode}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                          <Label htmlFor="emergency" className="text-sm font-semibold text-gray-800 dark:text-gray-100 flex items-center space-x-2">
                            <Users className="w-4 h-4 text-orange-500" />
                            <span>{t('workspace.emergency_contact_name', 'Emergency Contact Name')}</span>
                          </Label>
                          <Input id="emergency" placeholder={t('workspace.emergency_contact_name_1', 'Emergency contact name')} className="mt-2 border-2 border-orange-300 focus:border-orange-500 focus:ring-4 focus:ring-orange-100 rounded-xl h-10" />
                      </div>
                      <div>
                          <Label htmlFor="emergency-phone" className="text-sm font-semibold text-gray-800 dark:text-gray-100 flex items-center space-x-2">
                            <Phone className="w-4 h-4 text-orange-500" />
                            <span>{t('workspace.emergency_phone', 'Emergency Phone')}</span>
                          </Label>
                          <Input id="emergency-phone" placeholder="(555) 987-6543" className="mt-2 border-2 border-orange-300 focus:border-orange-500 focus:ring-4 focus:ring-orange-100 rounded-xl h-10" />
                        </div>
                      </div>
                      <Button className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-sm hover:shadow-md transition-shadow rounded-xl py-3 font-semibold">
                        {t('workspace.save_contact_information', 'Save Contact Information')}
                      </Button>
                  </CardContent>
                </Card>
              )}

              {/* Documents Section */}
              {activeSection === 'documents' && (
                  <Card className="border border-blue-200/50 dark:border-gray-700 shadow-xl bg-white dark:bg-gray-800 rounded-2xl overflow-hidden min-h-[600px]">
                    <CardHeader className="pb-4 bg-gradient-to-r from-blue-50/90 to-indigo-50/90 border-b border-blue-200/40">
                      <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center space-x-2 text-lg font-semibold text-gray-800 dark:text-gray-100">
                          <div className="p-2 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl shadow-sm">
                            <FileText className="w-5 h-5 text-white" />
                          </div>
                          <span>{t('workspace.case_documents', 'Case Documents')}</span>
                        </CardTitle>
                        <div>
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileUpload}
                            className="hidden"
                            accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                          />
                          <Button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploadingFile}
                            className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-sm hover:shadow-md transition-shadow rounded-xl px-4 py-2 flex items-center space-x-2"
                          >
                            <Upload className="w-4 h-4" />
                            <span>{uploadingFile ? 'Uploading...' : 'Upload Document'}</span>
                          </Button>
                        </div>
                      </div>
                  </CardHeader>
                    <CardContent className="p-6">
                      {documents.length === 0 ? (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 bg-gradient-to-br from-slate-400 to-slate-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm">
                          <FileText className="w-8 h-8 text-white" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-2">{t('workspace.no_documents_uploaded', 'No documents uploaded')}</h3>
                        <p className="text-sm text-slate-600 mb-4">
                        {t('workspace.upload_relevant_documents_for_this', 'Upload relevant documents for this case')}
                      </p>
                      </div>
                      ) : (
                        <div className="space-y-3">
                          {documents.map((doc) => (
                            <div
                              key={doc.id}
                              className="flex items-center justify-between p-4 bg-gradient-to-r from-slate-50 to-gray-50 border border-slate-200 rounded-xl hover:shadow-sm transition-all duration-200"
                            >
                              <div className="flex items-center space-x-3 flex-1">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                  <FileText className="w-5 h-5 text-blue-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="font-semibold text-gray-800 dark:text-gray-100 truncate">{doc.filename}</div>
                                  <div className="text-xs text-gray-600 dark:text-gray-400">
                                    {formatFileSize(doc.file_size)} • {fmt.short(doc.uploaded_at)}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handlePreviewDocument(doc)}
                                  className="rounded-lg"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDownloadDocument(doc)}
                                  className="rounded-lg"
                                >
                                  <Download className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDeleteDocument(doc.id)}
                                  className="rounded-lg text-red-600 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                  </CardContent>
                </Card>
              )}

              {/* Provider Acceptance Section */}
              {activeSection === 'acceptance' && (
                <Card className="border border-purple-200/50 dark:border-gray-700 shadow-xl bg-white dark:bg-gray-800 rounded-2xl overflow-hidden">
                  <CardHeader className="pb-4 bg-gradient-to-r from-purple-50/90 to-indigo-50/90 border-b border-purple-200/40">
                    <CardTitle className="flex items-center space-x-2 text-lg font-semibold text-gray-800 dark:text-gray-100">
                      <div className="p-2 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-xl shadow-sm">
                        <UserCheck className="w-5 h-5 text-white" />
                      </div>
                      <span>{t('workspace.provider_acceptance', 'Provider Acceptance & Response')}</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    {/* Show acceptance workflow for Pending/Matched status */}
                    {(selectedReferral.status === "Pending" || selectedReferral.status === "Matched" || selectedReferral.status === "Auto-Matched") ? (
                      <>
                        {/* PHI Disclosure Notice */}
                        <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                          <div className="flex items-start gap-3">
                            <Shield className="w-5 h-5 text-purple-600 mt-0.5" />
                            <div>
                              <p className="font-semibold text-purple-900">{t('workspace.phi_secure_delivery', 'Secure PHI Delivery')}</p>
                              <p className="text-sm text-purple-700 mt-1">
                                {t('workspace.phi_secure_delivery_description', 'Upon provider acceptance, complete member details including PHI will be securely sent to the provider\'s registered email address.')}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Use ReferralAcceptance for care referrals */}
                        {selectedReferral.matchedProviderId ? (
                          <ReferralAcceptance
                            referralId={String(selectedReferral.id)}
                            providerId={selectedReferral.matchedProviderId}
                            onAccept={() => {
                              console.log('Referral accepted');
                              window.location.reload();
                            }}
                            onReject={() => {
                              console.log('Referral rejected');
                              window.location.reload();
                            }}
                          />
                        ) : (
                          <div className="space-y-4">
                            <p className="text-sm text-gray-600 mb-4">
                              {matchedProviders.length > 0
                                ? 'Matched providers for referral acceptance:'
                                : 'Use the demo provider below to test the referral acceptance workflow:'}
                            </p>

                            {/* Demo: Trust Referral Provider - Always visible for demo purposes */}
                            <div className="p-4 border-2 border-green-300 rounded-lg bg-green-50/50">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <p className="font-semibold text-gray-800">Trust Referral Provider</p>
                                      <Badge className="bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-200 text-xs">Demo Provider</Badge>
                                    </div>
                                    <p className="text-sm text-gray-600">Match Score: 95.0%</p>
                                    <p className="text-xs text-gray-500 mt-1">vgulati@trust.care</p>
                                  </div>
                                  <Badge className={`${
                                    providerResponseStatus['trust-demo'] === 'accepted' ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-200' :
                                    providerResponseStatus['trust-demo'] === 'rejected' ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-200' :
                                    providerResponseStatus['trust-demo'] === 'notified' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-200' :
                                    'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-200'
                                  }`}>
                                    {providerResponseStatus['trust-demo'] || 'pending'}
                                  </Badge>
                                </div>

                                {/* Workflow Actions */}
                                <div className="mt-4 pt-4 border-t border-green-200 space-y-3">
                                  {/* Accept/Reject Dialog */}
                                  {showAcceptDialog === 'trust-demo' && (
                                    <div className="p-4 bg-white border-2 border-green-300 rounded-lg space-y-3">
                                      <div className="flex items-center gap-2 text-green-700">
                                        <CheckCircle className="w-5 h-5" />
                                        <p className="font-semibold">Confirm Acceptance</p>
                                      </div>
                                      <p className="text-sm text-gray-600">
                                        Accept this referral on behalf of Trust Referral Provider?
                                        PHI will be securely sent to the provider upon acceptance.
                                      </p>
                                      <div className="flex gap-2">
                                        <Button
                                          onClick={() => handleDemoAccept('trust-demo', 'Trust Referral Provider')}
                                          disabled={sendingRequest === 'trust-demo'}
                                          className="bg-v2accent hover:bg-v2accent/90 text-white"
                                        >
                                          {sendingRequest === 'trust-demo' ? (
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                          ) : (
                                            <CheckCircle className="w-4 h-4 mr-2" />
                                          )}
                                          Confirm Accept
                                        </Button>
                                        <Button
                                          variant="outline"
                                          onClick={() => setShowAcceptDialog(null)}
                                        >
                                          Cancel
                                        </Button>
                                      </div>
                                    </div>
                                  )}

                                  {showRejectDialog === 'trust-demo' && (
                                    <div className="p-4 bg-white border-2 border-red-300 rounded-lg space-y-3">
                                      <div className="flex items-center gap-2 text-red-700">
                                        <XCircle className="w-5 h-5" />
                                        <p className="font-semibold">Confirm Rejection</p>
                                      </div>
                                      <div>
                                        <Label className="text-sm font-medium">Reason for rejection *</Label>
                                        <Input
                                          value={rejectReason}
                                          onChange={(e) => setRejectReason(e.target.value)}
                                          placeholder="e.g., Not accepting new patients"
                                          className="mt-1"
                                        />
                                      </div>
                                      <div className="flex gap-2">
                                        <Button
                                          onClick={() => handleDemoReject('trust-demo', 'Trust Referral Provider')}
                                          disabled={sendingRequest === 'trust-demo' || !rejectReason.trim()}
                                          className="bg-red-600 hover:bg-red-700 text-white"
                                        >
                                          {sendingRequest === 'trust-demo' ? (
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                          ) : (
                                            <XCircle className="w-4 h-4 mr-2" />
                                          )}
                                          Confirm Reject
                                        </Button>
                                        <Button
                                          variant="outline"
                                          onClick={() => {
                                            setShowRejectDialog(null);
                                            setRejectReason('');
                                          }}
                                        >
                                          Cancel
                                        </Button>
                                      </div>
                                    </div>
                                  )}

                                  {/* Action Buttons (when no dialog is open) */}
                                  {!showAcceptDialog && !showRejectDialog && (
                                    <>
                                      <p className="text-xs text-green-700 mb-3">
                                        Demo provider for testing referral acceptance workflow.
                                      </p>
                                      <div className="flex flex-wrap gap-2">
                                        <Button
                                          onClick={() => handleSendReferralRequest('trust-demo', 'Trust Referral Provider', 'vgulati@trust.care')}
                                          disabled={sendingRequest === 'trust-demo'}
                                          variant="outline"
                                          size="sm"
                                          className="border-blue-300 text-blue-700 hover:bg-blue-50"
                                        >
                                          {sendingRequest === 'trust-demo' ? (
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                          ) : (
                                            <Send className="w-4 h-4 mr-2" />
                                          )}
                                          Send Request
                                        </Button>
                                        <Button
                                          onClick={() => setShowAcceptDialog('trust-demo')}
                                          variant="outline"
                                          size="sm"
                                          className="border-green-300 text-green-700 hover:bg-green-50"
                                        >
                                          <CheckCircle className="w-4 h-4 mr-2" />
                                          Accept (Demo)
                                        </Button>
                                        <Button
                                          onClick={() => setShowRejectDialog('trust-demo')}
                                          variant="outline"
                                          size="sm"
                                          className="border-red-300 text-red-700 hover:bg-red-50"
                                        >
                                          <XCircle className="w-4 h-4 mr-2" />
                                          Reject (Demo)
                                        </Button>
                                      </div>
                                    </>
                                  )}
                                </div>
                            </div>

                            {matchedProviders.map((provider: any) => (
                              <div key={provider.match_id || provider.provider_id} className="p-4 border border-purple-200 rounded-lg bg-purple-50/30">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="font-semibold text-gray-800">{provider.provider_name}</p>
                                    <p className="text-sm text-gray-600">Match Score: {provider.match_score?.toFixed(1)}%</p>
                                    {provider.provider_email && (
                                      <p className="text-xs text-gray-500 mt-1">{provider.provider_email}</p>
                                    )}
                                  </div>
                                  <Badge className={`${
                                    provider.acceptance_status === 'accepted' ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-200' :
                                    provider.acceptance_status === 'rejected' ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-200' :
                                    'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-200'
                                  }`}>
                                    {provider.acceptance_status || 'pending'}
                                  </Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      /* Show acceptance history for Accepted/In Progress/Completed status */
                      <div className="space-y-6">
                        {/* Acceptance History Header */}
                        <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
                          <div className="p-2 bg-green-100 rounded-full">
                            <UserCheck className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">Provider Response History</h3>
                            <p className="text-sm text-gray-500">This referral has been processed</p>
                          </div>
                        </div>

                        {/* Accepted Provider Info */}
                        {selectedReferral.acceptanceStatus === 'accepted' && (
                          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex items-start gap-3">
                              <div className="p-2 bg-green-100 rounded-full flex-shrink-0">
                                <UserCheck className="w-5 h-5 text-green-600" />
                              </div>
                              <div className="flex-1">
                                <p className="font-semibold text-green-900">Referral Accepted</p>
                                {selectedReferral.acceptedAt && (
                                  <p className="text-sm text-green-700 mt-1">
                                    Accepted on {fmt.dateTime(selectedReferral.acceptedAt)}
                                  </p>
                                )}
                                {selectedReferral.phiEmailSent && selectedReferral.emailSentTo && (
                                  <p className="text-sm text-green-600 mt-2">
                                    ✉️ PHI sent to: {selectedReferral.emailSentTo}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Rejected Provider Info */}
                        {selectedReferral.acceptanceStatus === 'rejected' && (
                          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                            <div className="flex items-start gap-3">
                              <div className="p-2 bg-red-100 rounded-full flex-shrink-0">
                                <X className="w-5 h-5 text-red-600" />
                              </div>
                              <div className="flex-1">
                                <p className="font-semibold text-red-900">Referral Rejected</p>
                                {selectedReferral.rejectedAt && (
                                  <p className="text-sm text-red-700 mt-1">
                                    Rejected on {fmt.dateTime(selectedReferral.rejectedAt)}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Matched Providers History */}
                        {matchedProviders.length > 0 && (
                          <div className="space-y-3">
                            <h4 className="font-medium text-gray-700">Provider Response History</h4>
                            {matchedProviders.map((provider: any, idx: number) => (
                              <div key={idx} className={`p-3 rounded-lg border ${
                                provider.acceptance_status === 'accepted' ? 'bg-green-50 border-green-200' :
                                provider.acceptance_status === 'rejected' ? 'bg-red-50 border-red-200' :
                                'bg-gray-50 border-gray-200'
                              }`}>
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="font-medium text-gray-800">{provider.provider_name}</p>
                                    <p className="text-xs text-gray-500">
                                      {provider.accepted_at && `Accepted: ${fmt.dateTime(provider.accepted_at)}`}
                                      {provider.rejected_at && `Rejected: ${fmt.dateTime(provider.rejected_at)}`}
                                    </p>
                                  </div>
                                  <Badge className={`${
                                    provider.acceptance_status === 'accepted' ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-200' :
                                    provider.acceptance_status === 'rejected' ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-200' :
                                    'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
                                  }`}>
                                    {provider.acceptance_status || 'pending'}
                                  </Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          {/* Compact Sidebar - Takes 1 column */}
          <div className="space-y-6">
            {/* Case Status */}
              <Card className="border border-blue-200/50 dark:border-gray-700 shadow-xl bg-white dark:bg-gray-800 rounded-2xl overflow-hidden">
                <CardHeader className="pb-3 bg-gradient-to-r from-blue-50/90 to-indigo-50/90 border-b border-blue-200/40">
                  <CardTitle className="flex items-center space-x-2 text-base font-semibold text-gray-800 dark:text-gray-100">
                    <div className="p-2 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl shadow-sm">
                      <Target className="w-4 h-4 text-white" />
                    </div>
                    <span>{t('workspace.case_status', 'Case Status')}</span>
                  </CardTitle>
              </CardHeader>
                <CardContent className="p-4 space-y-3">
                <div>
                    <Label htmlFor="status" className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2 block">
                      {t('workspace.current_status', 'Current Status')}
                    </Label>
                  <Select value={caseStatus} onValueChange={(newStatus: "Pending" | "Matched" | "Accepted" | "Authorized" | "In Progress" | "On Hold" | "Completed") => {
                    setCaseStatus(newStatus);
                    // Auto-populate Next Appointment with current date/time when status changes
                    const now = new Date();
                    const formattedDateTime = now.toISOString().slice(0, 16);
                    setNextAppointment(formattedDateTime);
                  }}>
                      <SelectTrigger className="border-2 border-blue-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 rounded-xl h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pending">{t('workspace.pending_1', 'Pending')}</SelectItem>
                      <SelectItem value="Matched">{t('workspace.matched', 'Matched')}</SelectItem>
                      <SelectItem value="Accepted">{t('workspace.accepted_1', 'Accepted')}</SelectItem>
                      <SelectItem value="In Progress">{t('workspace.in_progress_1', 'In Progress')}</SelectItem>
                      <SelectItem value="On Hold">{t('workspace.on_hold_1', 'On Hold')}</SelectItem>
                      <SelectItem value="Completed">{t('workspace.completed_1', 'Completed')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                    <Label htmlFor="appointment" className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2 block flex items-center">
                      <Calendar className="w-3.5 h-3.5 mr-1.5" />
                      Date (Updated on Status Change)
                    </Label>
                  <Input
                    id="appointment"
                    type="datetime-local"
                    value={nextAppointment}
                    onChange={(e) => setNextAppointment(e.target.value)}
                      className="border-2 border-blue-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 rounded-xl h-10"
                  />
                </div>

                  <Button
                    onClick={async () => {
                      try {
                        console.log('🔄 Updating referral status...');
                        console.log('  Referral ID:', selectedReferralId);
                        console.log('  New Status:', caseStatus);
                        console.log('  Appointment:', nextAppointment);

                        const response = await authFetch(`${API_BASE_URL}/api/referrals/${selectedReferralId}`, {
                          method: 'PUT',
                          jsonBody: {
                            status: caseStatus,
                            updated_at: new Date().toISOString()
                          }
                        });

                        if (response.ok) {
                          console.log('✅ Status updated successfully');
                          alert(`Status updated to "${caseStatus}"!\n\nThis will now appear in:\n- Case Timeline\n- Capacity Dashboard (if Matched/Accepted)\n- Member Profile`);
                          // Reload the page to refresh data
                          window.location.reload();
                        } else {
                          const errorText = await response.text();
                          console.error('❌ Failed to update status:', response.status, errorText);
                          alert('Failed to update status. Please try again.');
                        }
                      } catch (error) {
                        console.error('❌ Error updating status:', error);
                        alert('Error updating status. Please try again.');
                      }
                    }}
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-sm hover:shadow-md transition-shadow rounded-xl py-2.5 font-semibold"
                  >
                    {t('workspace.update_status', 'Update Status')}
                  </Button>
              </CardContent>
            </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Document Preview Modal */}
      {previewDocument && (
        <div className="fixed inset-0 bg-black/50  z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex items-center space-x-3">
                <FileText className="w-6 h-6 text-blue-600" />
                <div>
                  <h3 className="font-bold text-gray-800 dark:text-gray-100">{previewDocument.filename}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{formatFileSize(previewDocument.file_size)}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDownloadDocument(previewDocument)}
                  className="rounded-lg"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {t('workspace.download', 'Download')}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPreviewDocument(null)}
                  className="rounded-lg"
                >
                  {t('workspace.close', 'Close')}
                </Button>
              </div>
            </div>
            <div className="p-4 overflow-auto max-h-[calc(90vh-100px)]">
              <div className="text-center text-gray-600 dark:text-gray-300">
                <p className="mb-4">Preview will be available for supported file types (PDF, images)</p>
                <Button
                  onClick={() => handleDownloadDocument(previewDocument)}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 rounded-xl px-6 py-3"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {t('workspace.download_to_view', 'Download to View')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
